/**
 * Typed API error with status, code, and data
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string,
    public data?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /**
   * Check if error is an ApiError
   */
  static isApiError(error: unknown): error is ApiError {
    return error instanceof ApiError;
  }

  /**
   * Check if error is unauthorized (401)
   */
  static isUnauthorized(error: unknown): boolean {
    return ApiError.isApiError(error) && error.status === 401;
  }

  /**
   * Check if error is forbidden (403)
   */
  static isForbidden(error: unknown): boolean {
    return ApiError.isApiError(error) && error.status === 403;
  }

  /**
   * Check if error is not found (404)
   */
  static isNotFound(error: unknown): boolean {
    return ApiError.isApiError(error) && error.status === 404;
  }

  /**
   * Check if error is a server error (5xx)
   */
  static isServerError(error: unknown): boolean {
    return (
      ApiError.isApiError(error) &&
      error.status !== undefined &&
      error.status >= 500
    );
  }
}

/**
 * Get API base URL based on environment
 */
export function getApiBaseUrl(): string {
  if (typeof window === "undefined") {
    // Server-side: use internal URL or environment variable
    return process.env.API_URL || "http://localhost:4000";
  }

  // Client-side: use public URL from Vite env
  return import.meta.env.VITE_API_URL || "http://localhost:4000";
}

/**
 * Core API request wrapper
 * Accepts optional Request for SSR cookie handling
 *
 * @example
 * // Client-side
 * const notes = await apiRequest<NoteTypes.Serialized[]>('/api/notes');
 *
 * // Server-side (via createServerFn handler)
 * const notes = await apiRequest<NoteTypes.Serialized[]>('/api/notes', {
 *   request: ctx.request, // Forward cookies from incoming request
 * });
 *
 * // With body
 * const note = await apiRequest<NoteTypes.Serialized>('/api/notes', {
 *   method: 'POST',
 *   body: { content: 'Hello' },
 * });
 */
export async function apiRequest<T = unknown>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    headers?: Record<string, string>;
    signal?: AbortSignal;
    request?: Request; // For SSR - extract cookies
  },
): Promise<T> {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}${path}`;

  const headers = new Headers(options?.headers);

  // Set Content-Type if body exists and not already set
  if (options?.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // Forward cookies from SSR request when provided
  if (options?.request) {
    const cookieHeader = options.request.headers.get("cookie");
    if (cookieHeader) {
      headers.set("Cookie", cookieHeader);
    }
  }

  try {
    const response = await fetch(url, {
      method: options?.method || "GET",
      headers,
      body: options?.body ? JSON.stringify(options.body) : undefined,
      credentials: "include", // Always include cookies for cookie-based auth
      signal: options?.signal,
    });

    // Handle errors
    if (!response.ok) {
      let errorData: unknown;
      const contentType = response.headers.get("content-type");

      if (contentType?.includes("application/json")) {
        errorData = await response.json();
      } else {
        errorData = await response.text();
      }

      throw new ApiError(
        `API request failed: ${response.statusText}`,
        response.status,
        undefined,
        errorData,
      );
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return null as T;
    }

    // Parse JSON response
    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      return await response.json();
    }

    // Return text for non-JSON responses
    return (await response.text()) as T;
  } catch (error) {
    // Re-throw ApiErrors as-is
    if (error instanceof ApiError) {
      throw error;
    }

    // Handle network errors
    if (error instanceof Error) {
      if (error.name === "AbortError") {
        throw new ApiError("Request aborted", undefined, "ABORTED");
      }

      throw new ApiError(
        `Network error: ${error.message}`,
        undefined,
        "NETWORK_ERROR",
        error,
      );
    }

    // Unknown error
    throw new ApiError(
      "An unknown error occurred",
      undefined,
      "UNKNOWN_ERROR",
      error,
    );
  }
}

/**
 * Streaming helper for AI endpoints
 * Returns an async generator that yields chunks as they arrive
 *
 * @example
 * for await (const chunk of streamApiRequest('/api/stream', { messages })) {
 *   console.log(chunk);
 * }
 */
export async function* streamApiRequest(
  path: string,
  body: unknown,
  options?: {
    headers?: Record<string, string>;
    signal?: AbortSignal;
  },
): AsyncGenerator<string, void, unknown> {
  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}${path}`;

  const headers = new Headers(options?.headers);
  headers.set("Content-Type", "application/json");

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    credentials: "include",
    signal: options?.signal,
  });

  if (!response.ok) {
    throw new ApiError(
      `Streaming request failed: ${response.statusText}`,
      response.status,
    );
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new ApiError("Response body is not readable", undefined, "NO_READER");
  }

  const decoder = new TextDecoder();

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      yield chunk;
    }
  } finally {
    reader.releaseLock();
  }
}
